import axios from "axios";
import { AdvanceBookingRequestListResponse, AdvanceBookingResponse, calculateOverdueAmountForAdvanceBookingResponse, CheckSlotAvailabilityResponse, RaisedBookingRequestFormData } from "../shared/interface/advanceBookingRequest";
import { RSAEncryption } from "../util/Cryptography";
import { KeyFobListApiResponse } from "../shared/interface/MasterManagement/keyFobMaster.interface";
import { AdvanceBookingPenaltyReportResponse, AdvanceBookingReportResponse, paramsObject } from "../shared/interface/ABRReport.interface";
import { PaymentGatewayConstant } from "../constants/DatabaseConstants/ABRConstants";

export const getABRList = async (params: { fromDate?: string | null; toDate?: string | null; customerId: number; locationId: number }) => {
  try {
    let query = `${process.env.REACT_APP_API_URL}/advance-booking-request/list?customerId=${params.customerId}&locationId=${params.locationId}`;

    const queryParams: string[] = [];

    if (params.fromDate) {
      queryParams.push(`fromDate=${encodeURIComponent(params.fromDate)}`);
    }

    if (params.toDate) {
      queryParams.push(`toDate=${encodeURIComponent(params.toDate)}`);
    }

    if (queryParams.length > 0) {
      query += `&${queryParams.join("&")}`;
    }

    const res = await axios.get(query);

    return res.data as AdvanceBookingRequestListResponse;
  } catch (error) {
    throw error;
  }
};

export const getAbrDetailsAPI = async (id: number) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/advance-booking-request/details/${id}`);

    return response.data as AdvanceBookingResponse;
  } catch (error) {
    throw error;
  }
};

export const cancelAbrRequestAPI = async (id: number) => {
  try {
    const response = await axios.patch(`${process.env.REACT_APP_API_URL}/advance-booking-request/cancel/${id}`, {});

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const markAbrRequestCompleteAPI = async (abr_id: number, data: { is_penalty: boolean; is_valet_parking: boolean; overdue_amount: number }) => {
  try {
    const response = await axios.patch(`${process.env.REACT_APP_API_URL}/advance-booking-request/complete/${abr_id}`, data);

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const raiseParkingBookingRequestAPI = async (
  abr_id: number,
  formData: {
    key_fob_id: number;
    vehicle_number: string;
    booking_id: string;
    customer_id: number;
    customer_location_id: number;
  }
) => {
  try {
    /* If no formData is passed, set isBookingRequestExist: true to retry an existing booking request */
    const sendToObj = formData
      ? {
          ...formData,
          vehicle_number: RSAEncryption.encrypt(formData.vehicle_number)
        }
      : { isBookingRequestExist: true };

    const response = await axios.patch(`${process.env.REACT_APP_API_URL}/advance-booking-request/parking-booking-request/${abr_id}`, sendToObj);

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const raiseParkingRetrievalRequestAPI = async (abr_id: number) => {
  try {
    const response = await axios.patch(`${process.env.REACT_APP_API_URL}/advance-booking-request/parking-retrieval-request/${abr_id}`, {});

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const calculateOverdueAmountForAdvanceBookingAPI = async (abr_id: number) => {
  try {
    const response = await axios.patch(`${process.env.REACT_APP_API_URL}/advance-booking-request/calculate-overdue/${abr_id}`, {});

    return response.data as calculateOverdueAmountForAdvanceBookingResponse;
  } catch (error) {
    throw error;
  }
};

export const getKeyFobListByAbrId = async (abr_id: number) => {
  let query = `${process.env.REACT_APP_API_URL}/advance-booking-request/keyfob-list/${abr_id}`;

  try {
    const response = await axios.get(query);

    return response.data as KeyFobListApiResponse;
  } catch (error) {
    throw error;
  }
};

export const getABRReportList = async (params: paramsObject & { paymentGateway?: PaymentGatewayConstant }) => {
  let query = `${process.env.REACT_APP_API_URL}/advance-booking-request/report?date=${params.date}&customerId=${params.customerId}&locationId=${params.locationId}&page=${params.page}&pageSize=${params.pageSize}`;

  if (params.searchQuery) {
    query += `&searchQuery=${params.searchQuery}`;
  }

  if (params.status) {
    query += `&status=${params.status}`;
  }

  if (params.paymentGateway) {
    query += `&paymentGateway=${params.paymentGateway}`;
  }

  try {
    const response = await axios.get(query);

    return response.data as AdvanceBookingReportResponse;
  } catch (error) {
    throw error;
  }
};

export const getABRPenaltyCollectionReport = async (params: paramsObject & { fromDate: string | null; toDate: string }) => {
  let query = `${process.env.REACT_APP_API_URL}/advance-booking-request/penalty-collection-report?customerId=${params.customerId}&locationId=${params.locationId}&page=${params.page}&pageSize=${params.pageSize}`;

  if (params.searchQuery) {
    query += `&searchQuery=${params.searchQuery}`;
  }

  if (params.fromDate) {
    query += `&fromDate=${params.fromDate}`;
  }

  if (params.toDate) {
    query += `&toDate=${params.toDate}`;
  }

  try {
    const response = await axios.get(query);

    return response.data as AdvanceBookingPenaltyReportResponse;
  } catch (error) {
    throw error;
  }
};

export const checkAvailableSlots = async (params: { fromDate: string; toDate: string; vehicleTypeId: number; customerLocationId: number }) => {
  let query = `${process.env.REACT_APP_API_URL}/advance-booking-request/check-available-slots?customerLocationId=${params.customerLocationId}&vehicleTypeId=${params.vehicleTypeId}&fromDate=${params.fromDate}&toDate=${params.toDate}`;

  try {
    const response = await axios.get(query);

    return response.data as CheckSlotAvailabilityResponse;
  } catch (error) {
    throw error;
  }
};

export const raiseAdvanceBookingRequest = async (data: { customerLocationId: number; parkingStartDate: string; parkingEndDate: string; vehicleTypeId: number; payableAmount: number; isValetParking: boolean }) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/advance-booking-request/`, data);

    return response.data as {
      message: string;
      data: {
        advanceBookingRequest: {
          id: number;
          booking_id: string;
          vehicle_type_id: number;
        };
      };
    };
  } catch (error) {
    throw error;
  }
};
